/* @jsx h */
import { defineComponent, ref, h } from "vue";
import { LemmonModal } from "@app/vue/components/index";
import { api } from "@app/data/index";
import { useAuth, useShop, useToast } from "@app/vue/providers/index";
import { RestaurantSettingKey } from "@lemmonhq/types";
import { getBooleanSetting } from "@app/data/helpers/settings_helpers";

export type ConfirmReduceQuantitiesModalProps = {
  items: { id: number; quantity: number; name: string }[];
  tableId: number;
  onSuccess: () => void | Promise<void>;
  onClose: () => void;
};

export const ConfirmReduceQuantitiesModal =
  defineComponent<ConfirmReduceQuantitiesModalProps>({
    name: "ConfirmReduceQuantitiesModal",
    props: ["items", "tableId", "onSuccess", "onClose"],
    setup(props) {
      const updateKitchen = ref(false);
      const loading = ref(false);
      const shop = useShop();
      const toast = useToast();
      const { user } = useAuth();

      updateKitchen.value = getBooleanSetting(
        user.value?.restaurant_settings || [],
        RestaurantSettingKey.REDUCE_QUANTITIES_UPDATE_KITCHEN_DEFAULT,
        false,
      );

      const handleConfirm = async () => {
        try {
          loading.value = true;
          await api.reduceOrderItemsQuantityFromTable(
            props.tableId,
            props.items.map((item) => ({
              id: item.id,
              quantity: item.quantity,
            })),
            updateKitchen.value,
            shop,
          );
          await props.onSuccess();
        } catch (e: any) {
          console.error("ConfirmReduceQuantitiesModal", e);
          toast.showToast(e.message || "Failed to remove items table", {
            type: "error",
            icon: "table_restaurant",
          });
        } finally {
          loading.value = false;
        }
      };

      return () => (
        <LemmonModal
          title="Remove items"
          onClose={props.onClose}
          renderButtons={() => (
            <div class="p-3 lg:p-4 border-t  flex justify-end gap-2">
              <button class="btn btn-neutral" onClick={props.onClose}>
                Cancel
              </button>
              <button
                class="btn btn-error"
                disabled={loading.value}
                onClick={handleConfirm}
              >
                <span
                  class={`material-symbols-outlined text-xl ${loading.value ? "animate-spin" : ""}`}
                >
                  {loading.value ? "refresh" : "delete"}
                </span>
                {loading.value ? "Removing" : "Confirm"}
              </button>
            </div>
          )}
        >
          <div class="p-4 flex flex-col gap-4">
            <ul class="space-y-1 text-sm">
              {props.items.map((item) => (
                <li key={item.id} class="flex justify-between">
                  <span>{item.name}</span>
                  <span class="">×{item.quantity}</span>
                </li>
              ))}
            </ul>
            <label class="flex items-center gap-3 cursor-pointer select-none">
              <input
                type="checkbox"
                class="checkbox"
                checked={updateKitchen.value}
                onChange={(e) => {
                  updateKitchen.value = (e.target as HTMLInputElement).checked;
                }}
              />
              <span class="text-sm">Send cancellation ticket to kitchen</span>
            </label>
          </div>
        </LemmonModal>
      );
    },
  });
